/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useAppConfig } from '@airasia-rn/ui';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ENVIRONMENT_URLS, { parseEnv } from '../../util/EnvironmentURLs';
import { insertParamsInUrl } from '../../util/utils';
import { StationDataObject } from '../../types/FlightSearch.types';
import Constants from './constants';
import { RecentSearchesCarouselItemProps } from './types';

type OriginWithDates = StationDataObject & {
  departDate?: string;
  returnDate?: string;
};

type RecentPair = {
  id: string;
  origin: OriginWithDates;
  destination: StationDataObject;
};

type RecentSearchCardItem = RecentSearchesCarouselItemProps & {
  _pairId: string;
};

const formatTripDates = (departDate?: string, returnDate?: string) => {
  if (!departDate) return null;

  const depart = format(new Date(departDate), 'EEE, d MMM');

  if (!returnDate) {
    return depart;
  }

  const ret = format(new Date(returnDate), 'EEE, d MMM');
  return `${depart} – ${ret}`;
};

export const useRecentSearchesCarouselCards = (
  setCarouselData: React.Dispatch<React.SetStateAction<RecentSearchCardItem[]>>,
  onCardPress?: (pair: RecentPair) => void,
) => {
  const { config } = useAppConfig();
  const env = config.env;

  const { t } = useTranslation('flights');

  return useCallback(
    (pairs: RecentPair[]): RecentSearchCardItem[] =>
      pairs.map((pair) => {
        const destinationCode =
          pair.destination.StationCode?.toLowerCase?.() ?? '';

        const imageUri = insertParamsInUrl(
          ENVIRONMENT_URLS.RECENT_SEARCHES_DEST_IMAGE[parseEnv(env)],
          [destinationCode],
        );

        return {
          onPress: () => onCardPress?.(pair),
          _pairId: pair.id,
          model: pair as any,
          hasTopIcon: false,
          imageHeight: Constants.IMAGE_HEIGHT,
          imageWidth: Constants.IMAGE_HEIGHT,
          width: Constants.CARD_WIDTH,
          hasOutlineBorder: true,
          imageProps: {
            image: imageUri,
            cacheProps: {
              priority: 'normal',
              cachePolicy: 'immutable',
            },
            onError: () => {
              setCarouselData((curr) =>
                curr.map((item) =>
                  item._pairId === pair.id
                    ? {
                        ...item,
                        imageProps: {
                          uri: ENVIRONMENT_URLS.FLIGHTS_PLACEHOLDER[
                            parseEnv(env)
                          ],
                        } as any,
                      }
                    : item,
                ),
              );
            },
            height: Constants.IMAGE_HEIGHT,
            width: Constants.IMAGE_WIDTH,
          },
          contentDetailsPropsValues: {
            primaryText: insertParamsInUrl(Constants.PRIMARY_TEXT, [
              pair.origin.CityName,
              pair.destination.CityName,
            ]),
            secondaryText:
              formatTripDates(
                pair.origin?.departDate,
                pair.origin?.returnDate,
              ) ?? undefined,
            // ctaText: t('flightsSearchPage.main.createTripCTA'),
            onCTAPress() {},
            width: Constants.CARD_WIDTH,
            imageWidth: Constants.IMAGE_WIDTH,
          },
        };
      }),
    [setCarouselData, env, t],
  );
};
